import * as echarts from 'echarts';
import comOpt from './common-options.json';
import { merge } from 'lodash';
import utils from "@/utils";

const formatLabels = (data) => {
  return data.map((day) => {
    if (Number.isInteger(day.text)) {
      return {
        ...day,
        text: `${day.text}:00`,
      };
    } else {
      return day;
    }
  });
};

const create = ({ pages, ref, chartData, field, category, colors }) => {
  return new Promise((resolve, reject) => {
    if (!ref || !ref[0]) {
      return;
    }
    const formattedData = formatLabels(chartData.data);

    const categories = formattedData.map((item) => item[category]);

    // Prepare series data
    const series = [];
    const defaultColors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'];
    const colorValues = colors && Object.keys(colors).length > 0 
      ? Object.values(colors) 
      : defaultColors;

    chartData.graphs.forEach((page, index) => {
      const data = formattedData.map((item) => item[page.field]);

      const stackName = page.pageId;

      series.push({
        name: page.owner_name,
        type: 'bar',
        data: data,
        stack: stackName,
        itemStyle: {
          color: colorValues[index % colorValues.length],
          borderRadius: 3,
          borderWidth: 0.5,
          borderColor: '#fff',
        },
        emphasis: {
          disabled: true,
        },
        z: 220,
        barMaxWidth: 12,
      });
    });

    const tooltipFormatter = (params) => {
      if (params.length === 0) return '';
      const categoryValue = params[0].axisValue;
      let tooltipHtml = `<div class='tooltip'><span class='date'>${categoryValue}</span><ul class='tooltip-list'>`;
      let hasData = false;
    
      params.forEach((param) => {
        if (param.data !== 0) {
          hasData = true;
          const seriesIndex = param.seriesIndex;
          const page = chartData.graphs[seriesIndex];
          
          // Format the data value to 2 decimal places
          const formattedValue = param.data.toFixed(2);
    
          tooltipHtml += `<li data-value="${formattedValue}">
            <div class="soc-sea" style="color:${colorValues[seriesIndex]}">
              <span class="soc-ico soc-${page.social.toLowerCase()}"></span>
              ${page.title}
            </div>${formattedValue}%
          </li>`;
        }
      });
    
      tooltipHtml += '</ul></div>';
      return hasData ? tooltipHtml : '';
    };
    

    // Chart options
    const option = merge({}, comOpt, {
      
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          z: 210,
        },
        formatter: tooltipFormatter,
      },
      xAxis: {
        type: 'category',
        data: categories,
      },
      yAxis: {
        type: 'value',
      },
      series: series,
      color: colorValues,
    });

    const myChart = echarts.init(ref[0]);
    myChart.setOption(option);

    myChart.on('finished', () => {
      resolve({ chart: myChart });
    });

    myChart.on('error', (error) => {
      reject(error);
    });
    window.addEventListener('resize', () => {
      myChart.resize();
    });
  });
};

export async function exportChart({
  chart,
  container,
  chartData,
  pages,
  category = 'text',
  colors,
  field,
  type = 'svg'
}) {
  if (!chart) {
    return;
  }

  const filename = `stacked_chart_${type === 'csv' ? 'data_' : ''}${new Date().toISOString().split('T')[0]}`;

  if (type === 'svg') {
    if (!container) {
      return;
    }

    try {
      if (chart && chart.dispose) {
        chart.dispose();
      }
      
      echarts.dispose(container);
      
      const svgChart = echarts.init(container, null, { renderer: 'svg' });
      
      const formattedData = chartData && chartData.data ? formatLabels(chartData.data) : [];
      
      const categories = formattedData.map(item => item[category]);
      const series = [];
      const defaultColors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'];
      const colorValues = colors && Object.keys(colors).length > 0 
        ? Object.values(colors) 
        : defaultColors;
      
      if (chartData && chartData.graphs) {
        chartData.graphs.forEach((page, index) => {
          const data = formattedData.map(item => item[page.field]);
          const stackName = page.pageId;
          
          series.push({
            name: page.owner_name || `Series ${index+1}`,
            type: 'bar',
            data: data,
            stack: stackName,
            itemStyle: {
              color: colorValues[index % colorValues.length] || '#5470c6',
              borderRadius: 3,
              borderWidth: 0.5,
              borderColor: '#fff',
            },
            emphasis: {
              disabled: true,
            },
            z: 220,
            barMaxWidth: 12,
          });
        });
      }
      
      const option = merge({}, comOpt, {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
            z: 210,
          }
        },
        xAxis: {
          type: 'category',
          data: categories,
        },
        yAxis: {
          type: 'value',
        },
        series: series,
        color: colorValues,
        animation: false
      });
      
      svgChart.setOption(option);
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const svgElement = container.querySelector('svg');
      if (!svgElement) {
        throw new Error("SVG element not found in container");
      }
      
      const svgContent = svgElement.outerHTML;
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      utils.downloadFile(blob, `${filename}.svg`);
      
      if (svgChart) {
        svgChart.dispose();
      }
      
      const newChart = await create({
        pages,
        ref: [container],
        chartData,
        category,
        colors,
        field,
        renderer: 'canvas',
        animation: true
      });
      
      return newChart;
    } catch (error) {
      
      try {
        return await create({
          pages,
          ref: [container],
          chartData,
          category,
          colors,
          field,
          renderer: 'canvas',
          animation: true
        });
      } catch (revertError) {
      }
    }
  } else if (type === 'csv') {
    if (!chartData || !chartData.data || chartData.data.length === 0) {
      return;
    }
    
    const formattedData = formatLabels(chartData.data);
    const categories = formattedData.map(item => item[category]);
    const series = [];
    
    if (chartData.graphs) {
      chartData.graphs.forEach(page => {
        series.push({
          name: page.owner_name || page.title || page.field,
          data: formattedData.map(item => item[page.field] || 0)
        });
      });
    }
    
    utils.exportDataToCSV({
      series,
      categories,
      seriesNames: series.map(s => s.name),
      filename
    });
  }
}

export default create;
